import { useEffect, useState } from "react";
import { PinIcon, Users, Send } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ANNOUNCEMENTS } from "@/constants/testIds";
import { toast } from "sonner";

export default function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [message, setMessage] = useState("");
  const [recipientType, setRecipientType] = useState("all");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/announcements").then(({ data }) => setAnnouncements(data));
    api.get("/employees").then(({ data }) => setEmployees(data));
  };

  useEffect(load, []);

  const toggleEmployee = (id) =>
    setSelectedEmployees((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        message,
        recipient_type: recipientType,
        employee_ids: recipientType === "specific" ? selectedEmployees : undefined,
      };
      const { data } = await api.post("/announcements", payload);
      toast.success(data.message);
      setMessage("");
      setRecipientType("all");
      setSelectedEmployees([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-3xl">
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight">Avisos</h1>
        <p className="text-charcoal/60 mt-1">Comunicados informales — no reemplazan políticas ni evidencia legal.</p>

        <form onSubmit={handleSubmit} className="bg-card border border-petroleum/10 rounded-2xl p-6 mt-6 shadow-[0_8px_30px_rgba(14,79,79,0.06)]">
          <Textarea
            data-testid={ANNOUNCEMENTS.messageInput}
            required
            maxLength={280}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ej. Recuerden registrar su asistencia todos los días."
            className="bg-white mb-4 min-h-24"
          />

          <p className="text-sm font-semibold text-charcoal/80 mb-2">Destinatarios</p>
          <div className="flex flex-col gap-2 mb-4">
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <input
                data-testid={ANNOUNCEMENTS.recipientAllRadio}
                type="radio"
                name="recipient_type"
                checked={recipientType === "all"}
                onChange={() => setRecipientType("all")}
                className="accent-petroleum"
              />
              Todos los empleados
            </label>
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <input
                data-testid={ANNOUNCEMENTS.recipientSpecificRadio}
                type="radio"
                name="recipient_type"
                checked={recipientType === "specific"}
                onChange={() => setRecipientType("specific")}
                className="accent-petroleum"
              />
              Empleados específicos
            </label>
          </div>

          {recipientType === "specific" && (
            <div className="space-y-2 max-h-56 overflow-y-auto mb-4 bg-accent/30 rounded-lg p-3">
              {employees.map((emp) => (
                <label key={emp.id} className="flex items-center gap-3 text-sm cursor-pointer">
                  <Checkbox
                    data-testid={`${ANNOUNCEMENTS.recipientEmployeeCheckbox}-${emp.id}`}
                    checked={selectedEmployees.includes(emp.id)}
                    onCheckedChange={() => toggleEmployee(emp.id)}
                  />
                  {emp.full_name}
                </label>
              ))}
              {employees.length === 0 && <p className="text-xs text-charcoal/50">No hay empleados registrados.</p>}
            </div>
          )}

          <Button
            data-testid={ANNOUNCEMENTS.submitButton}
            type="submit"
            disabled={sending || !message.trim() || (recipientType === "specific" && selectedEmployees.length === 0)}
            className="w-full bg-terracotta hover:bg-terracotta/90 rounded-full h-11"
          >
            <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando..." : "Enviar aviso"}
          </Button>
        </form>

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-4">Avisos enviados</h2>
        <div className="space-y-3">
          {announcements.map((a) => (
            <div key={a.id} data-testid={ANNOUNCEMENTS.adminRow} className="bg-card border border-petroleum/10 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <PinIcon className="w-4 h-4 text-terracotta mt-1 shrink-0" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="text-sm text-charcoal">{a.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-charcoal/50">
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {a.recipient_type === "all" ? "Todos" : `${a.recipient_count} seleccionado(s)`}</span>
                    <span>Leído por {a.read_count} de {a.recipient_count}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {announcements.length === 0 && <p className="text-charcoal/50 text-sm">Aún no has enviado ningún aviso.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
